'use client';

import React, { useCallback, useId, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { Icon } from '../../icons';
import { Caption } from '../Caption/Caption';
import { InputText } from '../InputText/InputText';
import './InputUrlEdit.css';

export interface InputUrlEditProps {
  baseUrl: string;
  urlSegment: string;
  label?: string;
  caption?: React.ReactNode;
  placeholder?: string;
  disabled?: boolean;
  readonly?: boolean;
  message?: string;
  messageType?: '' | 'error' | 'success';
  id?: string;
  compressBase?: boolean;

  // Events
  onEditStart?: () => void;
  onEditSave?: (value: string) => void;
  onEditCancel?: () => void;
  onUrlCopied?: (fullUrl: string) => void;

  // Validation override
  validateSegment?: (value: string) => boolean;
}

const DEFAULT_VALIDATE = (value: string) => /^[a-zA-Z0-9-]+$/.test(value);

export const InputUrlEdit: React.FC<InputUrlEditProps> = ({
  baseUrl,
  urlSegment,
  label,
  caption,
  placeholder = 'Enter URL segment',
  disabled = false,
  readonly = false,
  message,
  messageType = '',
  id,
  compressBase = true,
  onEditStart,
  onEditSave,
  onEditCancel,
  onUrlCopied,
  validateSegment = DEFAULT_VALIDATE,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [tempValue, setTempValue] = useState<string>(urlSegment);
  const [copied, setCopied] = useState(false);
  const editInputRef = React.useRef<HTMLInputElement>(null);

  // Keep tempValue in sync when not editing
  React.useEffect(() => {
    if (!isEditing) setTempValue(urlSegment);
  }, [urlSegment, isEditing]);

  const fullUrl = useMemo(() => `${baseUrl}${urlSegment}`, [baseUrl, urlSegment]);
  const isValid = useMemo(() => validateSegment(tempValue), [tempValue, validateSegment]);
  const reactId = useId();
  const rootId = id ?? `input-url-edit-${reactId}`;
  const captionId = caption ? `${rootId}-caption` : undefined;
  const messageId = message ? `${rootId}-message` : undefined;

  const displayBase = useMemo(() => {
    if (!compressBase) return baseUrl;
    const stripProtocol = (value: string) => value.replace(/^https?:\/\//i, '');
    try {
      const parsed = new URL(baseUrl);
      const host = parsed.host;
      const pathname = parsed.pathname || '/';
      const ensureTrailingSlash = (p: string) => (p.endsWith('/') ? p : `${p}/`);
      const segments = pathname.split('/').filter(Boolean);

      if (segments.length === 0) {
        return `${host}/`;
      }
      if (segments.length === 1) {
        return `${host}${ensureTrailingSlash(`/${segments[0]}`)}`;
      }
      if (segments.length === 2) {
        return `${host}${ensureTrailingSlash(`/${segments[0]}/${segments[1]}`)}`;
      }
      const first = segments[0];
      const last = segments[segments.length - 1];
      return `${host}/${first}/…/${last}/`;
    } catch {
      return stripProtocol(baseUrl);
    }
  }, [baseUrl, compressBase]);

  const startEditing = useCallback(() => {
    if (disabled || readonly) return;
    setIsEditing(true);
    setTempValue(urlSegment);
    onEditStart?.();
  }, [disabled, readonly, urlSegment, onEditStart]);

  const cancelEdit = useCallback(() => {
    setIsEditing(false);
    setTempValue(urlSegment);
    onEditCancel?.();
  }, [urlSegment, onEditCancel]);

  const saveEdit = useCallback(() => {
    if (!isValid) return;
    setIsEditing(false);
    onEditSave?.(tempValue);
  }, [isValid, tempValue, onEditSave]);

  const copyUrl = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      onUrlCopied?.(fullUrl);
    } catch {
      // ignore
    } finally {
      window.setTimeout(() => setCopied(false), 1200);
    }
  }, [fullUrl, onUrlCopied]);

  // Keep input sizing managed by InputText; wrapper will flex naturally

  React.useEffect(() => {
    if (isEditing) {
      editInputRef.current?.focus();
    }
  }, [isEditing]);

  return (
    <motion.div className="input-url-edit" data-disabled={disabled} data-readonly={readonly} layout>
      {label && (
        <label className="input-url-edit__label" htmlFor={rootId}>
          {label}
        </label>
      )}

      {caption && (
        <Caption>
          <span id={captionId}>{caption}</span>
        </Caption>
      )}

      <div
        className="input-url-edit__container"
        role="group"
        aria-labelledby={label ? rootId : undefined}
        aria-describedby={[captionId, messageId].filter(Boolean).join(' ') || undefined}
      >
        <span className="input-url-edit__input-wrapper">
          <span className="input-url-edit__base" aria-label="Base URL">
            {displayBase}
          </span>

          <AnimatePresence initial={false} mode="wait">
            {!isEditing ? (
              <motion.button
                key="segment"
                type="button"
                className="input-url-edit__segment"
                onClick={startEditing}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    startEditing();
                  }
                }}
                aria-label="Edit URL segment"
                disabled={disabled || readonly}
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.98 }}
                transition={{ duration: 0.18, ease: 'easeOut' }}
                style={{ willChange: 'transform, opacity' }}
              >
                <span>{urlSegment}</span>
                {!readonly && !disabled && (
                  <Icon name="Pencil" className="input-url-edit__edit-icon" />
                )}
              </motion.button>
            ) : (
              <motion.div
                key="edit"
                className="input-url-edit__edit-row"
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.98 }}
                transition={{ duration: 0.18, ease: 'easeOut' }}
                style={{ willChange: 'transform, opacity' }}
                onAnimationComplete={() => {
                  if (isEditing) {
                    // Defer focus until after animation flushes to ensure the input is in the DOM
                    window.requestAnimationFrame(() => editInputRef.current?.focus());
                  }
                }}
              >
                <span className="input-url-edit__input-wrapper">
                  <InputText
                    id={rootId}
                    ref={editInputRef}
                    value={tempValue}
                    onChange={(e) => setTempValue(e.target.value)}
                    onBlur={cancelEdit}
                    placeholder={placeholder}
                    disabled={disabled}
                    readonly={readonly}
                    size="sm"
                    trailingAction="custom"
                    trailingActionIconName="Check"
                    trailingActionAriaLabel={isValid ? 'Save' : 'Save (disabled)'}
                    trailingActionDisabled={!isValid}
                    onActionClicked={saveEdit}
                    secondaryTrailingActionIconName="X"
                    secondaryTrailingActionAriaLabel="Cancel"
                    onSecondaryActionClicked={cancelEdit}
                    fullWidth
                  />
                </span>
              </motion.div>
            )}
          </AnimatePresence>
        </span>

        <button
          type="button"
          className="input-url-edit__copy"
          aria-label="Copy URL"
          onClick={copyUrl}
          disabled={disabled || readonly}
        >
          <span style={{ position: 'relative', display: 'inline-flex', width: 20, height: 20 }}>
            <motion.span
              initial={false}
              animate={copied ? { opacity: 0, scale: 0.6 } : { opacity: [0, 1], scale: [0.8, 1] }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
              style={{
                position: 'absolute',
                inset: 0,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                willChange: 'transform, opacity',
              }}
            >
              <Icon name="Copy" aria-hidden="true" />
            </motion.span>
            <motion.span
              initial={false}
              animate={
                copied
                  ? { opacity: [0, 1, 1], scale: [0.6, 1.12, 1] }
                  : { opacity: [1, 0], scale: [1, 0.8] }
              }
              transition={
                copied
                  ? { duration: 0.32, times: [0, 0.5, 1], ease: 'easeOut' }
                  : { duration: 0.2, ease: 'easeOut' }
              }
              style={{
                position: 'absolute',
                inset: 0,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                willChange: 'transform, opacity',
              }}
            >
              <Icon name="Check" className="success" aria-hidden="true" />
            </motion.span>
          </span>
        </button>
      </div>

      {message && (
        <span id={messageId} className="input-url-edit__message" data-type={messageType}>
          {message}
        </span>
      )}
    </motion.div>
  );
};

InputUrlEdit.displayName = 'InputUrlEdit';
